/**
 * LeadsView — admin-only "Access Requests" pipeline (lightweight CRM).
 *
 * Reads the inbound leads captured by the marketing site's Request-access form
 * (`/admin/access-requests`, see `src/api/leads.ts`). ADMIN role is enforced
 * server-side; this view also runs a local access guard as defense-in-depth.
 *
 * Layout: header · toolbar (search + status/persona filters + CSV export) ·
 * table with inline status change · click-a-row drawer for full detail + notes.
 * Pattern mirrors DataChangesView: axios + useState + useEffect (not React
 * Query), design-system tokens, ViewHeader.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus, Download, Search } from 'lucide-react';
import {
  fetchAccessRequests,
  updateAccessRequest,
  LEAD_STATUSES,
  type AccessRequest,
  type LeadStatus,
  type LeadPersona,
  type LeadSortKey,
} from '@/api/leads';
import type { ListEnvelope } from '@/api/dataChanges';
import { Skeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import { ViewHeader } from '@/components/ViewHeader';

const PAGE_SIZE = 50;

const STATUS_META: Record<LeadStatus, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'bg-status-info-bg/40 border-status-info-bg/50 text-status-info' },
  contacted: { label: 'Contacted', cls: 'bg-status-warning-bg/40 border-status-warning-bg/50 text-status-warning' },
  qualified: { label: 'Qualified', cls: 'bg-status-success-bg/40 border-status-success-bg/50 text-status-success' },
  closed: { label: 'Closed', cls: 'bg-bg-elevated border-border-base text-fg-muted' },
  spam: { label: 'Spam', cls: 'bg-status-danger-bg/40 border-status-danger-bg/50 text-status-danger' },
};

function personaLabel(p: LeadPersona): string {
  return p === 'campaign' ? 'Campaign' : 'Committee / Data';
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface LeadsViewProps {
  userRole?: string;
}

export function LeadsView({ userRole }: LeadsViewProps) {
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'ADMIN') {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access Denied</h2>
          <p className="text-fg-secondary">
            Access Requests are available to administrators only.
          </p>
        </div>
      </div>
    );
  }

  const [envelope, setEnvelope] = useState<ListEnvelope<AccessRequest> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [personaFilter, setPersonaFilter] = useState<LeadPersona | ''>('');
  const [sort, setSort] = useState<LeadSortKey>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const [selected, setSelected] = useState<AccessRequest | null>(null);
  const [exporting, setExporting] = useState(false);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [q, statusFilter, personaFilter, sort, order]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAccessRequests({
      q: q || undefined,
      status: statusFilter || undefined,
      persona: personaFilter || undefined,
      sort,
      order,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((env) => {
        if (cancelled) return;
        setEnvelope(env);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const s = e?.response?.status;
        setError(
          s === 403
            ? 'Your role does not have access to leads.'
            : s
              ? `Backend error ${s}.`
              : 'Could not reach the backend.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [q, statusFilter, personaFilter, sort, order, page, reload]);

  const changeStatus = useCallback(
    async (row: AccessRequest, next: LeadStatus) => {
      // optimistic
      setEnvelope((env) =>
        env
          ? { ...env, items: env.items.map((r) => (r.id === row.id ? { ...r, status: next } : r)) }
          : env,
      );
      try {
        const updated = await updateAccessRequest(row.id, { status: next });
        setEnvelope((env) =>
          env ? { ...env, items: env.items.map((r) => (r.id === updated.id ? updated : r)) } : env,
        );
        setSelected((s) => (s && s.id === updated.id ? updated : s));
      } catch {
        setReload((n) => n + 1); // reconcile from server on failure
      }
    },
    [],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const env = await fetchAccessRequests({
        q: q || undefined,
        status: statusFilter || undefined,
        persona: personaFilter || undefined,
        sort,
        order,
        page: 1,
        pageSize: 500,
      });
      const cols = ['created_at', 'name', 'email', 'organization', 'persona', 'status', 'message', 'notes'];
      const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = [
        cols.join(','),
        ...env.items.map((r) => cols.map((c) => esc((r as any)[c])).join(',')),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-requests_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // eslint-disable-next-line no-alert
      alert('Export failed — try again.');
    } finally {
      setExporting(false);
    }
  }, [q, statusFilter, personaFilter, sort, order]);

  const items = envelope?.items ?? [];
  const total = envelope?.total ?? 0;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  const hasNext = page * PAGE_SIZE < total;
  const hasPrev = page > 1;

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-accent" />
            Access Requests
          </span>
        }
        tagline="Inbound leads from the voterra.io marketing site"
        actions={
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="inline-flex items-center gap-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-50 disabled:cursor-not-allowed text-fg-primary text-xs font-semibold px-3 py-2"
            title="Export filtered leads to CSV"
          >
            <Download className="w-3.5 h-3.5" aria-hidden="true" />
            {exporting ? 'Preparing…' : 'Export CSV'}
          </button>
        }
      />

      {/* Toolbar */}
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">Search</span>
          <span className="relative">
            <Search className="w-4 h-4 text-fg-muted absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, email, or organization…"
              className="w-full bg-bg-base border border-border-base rounded pl-8 pr-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
            />
          </span>
        </label>
        <FilterSelect label="Status" value={statusFilter} onChange={(v) => setStatusFilter(v as LeadStatus | '')}
          options={[{ value: '', label: 'All' }, ...LEAD_STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))]} />
        <FilterSelect label="Persona" value={personaFilter} onChange={(v) => setPersonaFilter(v as LeadPersona | '')}
          options={[{ value: '', label: 'All' }, { value: 'campaign', label: 'Campaign' }, { value: 'committee', label: 'Committee / Data' }]} />
        <FilterSelect label="Sort" value={`${sort}:${order}`} onChange={(v) => { const [s, o] = v.split(':'); setSort(s as LeadSortKey); setOrder(o as 'asc' | 'desc'); }}
          options={[
            { value: 'created_at:desc', label: 'Newest' },
            { value: 'created_at:asc', label: 'Oldest' },
            { value: 'status:asc', label: 'Status' },
            { value: 'persona:asc', label: 'Persona' },
          ]} />
      </div>

      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setReload((n) => n + 1)} className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold">Retry</button>
        </div>
      )}

      {loading && !envelope && (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
      )}

      {!loading && !error && items.length === 0 && (
        <EmptyState icon={UserPlus} title="No access requests yet" description="When someone submits the Request-access form on voterra.io, they'll show up here." />
      )}

      {items.length > 0 && (
        <div className="bg-bg-raised rounded-xl border border-border-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base text-left text-fg-muted">
                  <th className="py-2.5 px-4 font-semibold">Received</th>
                  <th className="py-2.5 px-4 font-semibold">Prospect</th>
                  <th className="py-2.5 px-4 font-semibold">Organization</th>
                  <th className="py-2.5 px-4 font-semibold">Interest</th>
                  <th className="py-2.5 px-4 font-semibold">Status</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Detail</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-border-base last:border-0 hover:bg-bg-base/40">
                    <td className="py-2.5 px-4 text-fg-muted text-xs tabular-nums whitespace-nowrap">{formatWhen(row.created_at)}</td>
                    <td className="py-2.5 px-4">
                      <div className="text-fg-primary font-medium">{row.name}</div>
                      <a href={`mailto:${row.email}`} className="text-[12px] text-status-info hover:underline">{row.email}</a>
                    </td>
                    <td className="py-2.5 px-4 text-fg-secondary">{row.organization || '—'}</td>
                    <td className="py-2.5 px-4">
                      <span className="inline-block px-2 py-0.5 rounded border border-border-base text-[11px] text-fg-secondary whitespace-nowrap">{personaLabel(row.persona)}</span>
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusSelect value={row.status} onChange={(next) => changeStatus(row, next)} />
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <button onClick={() => setSelected(row)} className="text-xs text-status-info hover:underline">Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-2 px-4 py-3 text-xs text-fg-muted border-t border-border-base">
            <div>Showing {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!hasPrev || loading} className="px-3 py-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 text-fg-primary">← Prev</button>
              <span className="tabular-nums">Page {page}</span>
              <button onClick={() => setPage((p) => p + 1)} disabled={!hasNext || loading} className="px-3 py-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 text-fg-primary">Next →</button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onStatus={(next) => changeStatus(selected, next)}
          onSaveNotes={async (notes) => {
            const updated = await updateAccessRequest(selected.id, { notes });
            setSelected(updated);
            setEnvelope((env) => (env ? { ...env, items: env.items.map((r) => (r.id === updated.id ? updated : r)) } : env));
          }}
        />
      )}
    </div>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-bg-base border border-border-base rounded px-2 py-2 text-fg-primary text-sm min-w-[130px]">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function StatusSelect({ value, onChange }: { value: LeadStatus; onChange: (v: LeadStatus) => void }) {
  const meta = STATUS_META[value];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as LeadStatus)}
      className={`rounded border text-[11px] font-bold uppercase tracking-wide px-2 py-1 cursor-pointer ${meta.cls}`}
      title="Change status"
    >
      {LEAD_STATUSES.map((s) => <option key={s} value={s} className="bg-bg-base text-fg-primary normal-case">{STATUS_META[s].label}</option>)}
    </select>
  );
}

function LeadDrawer({
  lead, onClose, onStatus, onSaveNotes,
}: {
  lead: AccessRequest;
  onClose: () => void;
  onStatus: (next: LeadStatus) => void;
  onSaveNotes: (notes: string) => Promise<void>;
}) {
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setNotes(lead.notes ?? '');
    setSaved(false);
  }, [lead.id, lead.notes]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await onSaveNotes(notes);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black/50 z-40" aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-label="Lead detail" className="fixed inset-y-0 right-0 w-full max-w-md bg-bg-base border-l border-border-base shadow-2xl z-50 flex flex-col">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border-base bg-bg-raised/60">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-accent font-semibold mb-0.5">{personaLabel(lead.persona)}</div>
            <h2 className="text-xl font-bold text-fg-primary truncate">{lead.name}</h2>
            <a href={`mailto:${lead.email}`} className="text-xs text-status-info hover:underline break-all">{lead.email}</a>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <Field label="Organization" value={lead.organization || '—'} />
          <Field label="Received" value={formatWhen(lead.created_at)} />
          {lead.contacted_at && <Field label="First contacted" value={formatWhen(lead.contacted_at)} />}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Status</div>
            <StatusSelect value={lead.status} onChange={onStatus} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Message</div>
            <div className="rounded border border-border-base bg-bg-raised p-3 text-sm text-fg-secondary whitespace-pre-wrap min-h-[3rem]">{lead.message || <span className="text-fg-muted">— no message —</span>}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Internal notes</div>
            <textarea value={notes} onChange={(e) => { setNotes(e.target.value); setSaved(false); }} rows={4} placeholder="Add a note (visible to admins only)…" className="w-full bg-bg-raised border border-border-base rounded p-3 text-sm text-fg-primary placeholder-fg-muted" />
            <div className="flex items-center gap-3 mt-2">
              <button onClick={save} disabled={saving} className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-white px-3 py-1.5 rounded text-xs font-bold">{saving ? 'Saving…' : 'Save notes'}</button>
              {saved && <span className="text-xs text-status-success">Saved</span>}
            </div>
          </div>
          {(lead.source || lead.referrer || lead.ip) && (
            <div className="pt-2 border-t border-border-base space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">Capture context</div>
              {lead.source && <div className="text-[11px] text-fg-muted font-mono">source: {lead.source}</div>}
              {lead.referrer && <div className="text-[11px] text-fg-muted font-mono break-all">ref: {lead.referrer}</div>}
              {lead.ip && <div className="text-[11px] text-fg-muted font-mono">ip: {lead.ip}</div>}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-0.5">{label}</div>
      <div className="text-sm text-fg-primary">{value}</div>
    </div>
  );
}

export default LeadsView;
